import {
  TOP_FILTER_OPTIONS,
  SUBFILTER_OPTIONS,
  topFilterToDefaultSubFilter,
  type TopFilter,
  type DraftsTopFilter,
} from "../lib/caseTaxonomyFilter";
import { SlidingPillSegmentedControl } from "./SlidingPillSegmentedControl";

type ExtraTopFilter = { id: string; label: string };

type SubFilterMap = Record<Exclude<TopFilter, "All" | "Other">, readonly string[]>;

type Props = {
  topFilter: DraftsTopFilter;
  subFilter: string;
  onTopFilterChange: (top: DraftsTopFilter) => void;
  onSubFilterChange: (sub: string) => void;
  /** Unique scope per page so the pill does not fly across route changes (e.g. "cases", "drafts"). */
  pillScope?: string;
  extraTopFilters?: ExtraTopFilter[];
  /** Override case sub-filters (e.g. statute subject-matter chips on Library). */
  subFilterOptionsMap?: SubFilterMap;
  /** Override default sub-filter when a top filter is selected. */
  resolveDefaultSubFilter?: (top: TopFilter) => string;
  /** Sub-chips for top = Other (e.g. General Law / Evidence on Library). */
  otherSubFilterOptions?: readonly string[];
};

export default function CaseCategoryFilter({
  topFilter,
  subFilter,
  onTopFilterChange,
  onSubFilterChange,
  pillScope = 'category',
  extraTopFilters = [],
  subFilterOptionsMap = SUBFILTER_OPTIONS,
  resolveDefaultSubFilter = topFilterToDefaultSubFilter,
  otherSubFilterOptions,
}: Props) {
  const handleTop = (top: DraftsTopFilter) => {
    onTopFilterChange(top);
    if (top !== "Unlinked" && top !== "All") {
      if (top === "Other" && otherSubFilterOptions?.length) {
        onSubFilterChange(otherSubFilterOptions[0]);
      } else if (top !== "Other") {
        onSubFilterChange(resolveDefaultSubFilter(top as TopFilter));
      }
    }
  };

  const subOptions =
    topFilter === "Other" && otherSubFilterOptions?.length
      ? otherSubFilterOptions
      : topFilter !== "All" &&
          topFilter !== "Other" &&
          topFilter !== "Unlinked" &&
          topFilter in subFilterOptionsMap
        ? subFilterOptionsMap[topFilter as Exclude<TopFilter, "All" | "Other">]
        : null;

  const topSegments = [
    ...TOP_FILTER_OPTIONS.map((top) => ({ id: top, label: top })),
    ...extraTopFilters.map(({ id, label }) => ({ id, label })),
  ];

  return (
    <div className="flex flex-col gap-3">
      <SlidingPillSegmentedControl
        value={topFilter}
        onChange={(id) => handleTop(id as DraftsTopFilter)}
        segments={topSegments}
        variant="primary"
        layoutId={`${pillScope}-category-top-pill`}
        staticHighlight
      />

      {subOptions && (
        <SlidingPillSegmentedControl
          value={subFilter}
          onChange={onSubFilterChange}
          segments={subOptions.map((sf) => ({ id: sf, label: sf }))}
          variant="sub"
          staticHighlight
        />
      )}
    </div>
  );
}

